import { isPWA } from 'mini-device-utils/lib/utils/isPWA';
import { getPackageBaseUrl } from 'owa-config';
import { getOwaWorkload } from 'owa-workloads';
import OwaWorkload from 'owa-workloads/lib/store/schema/OwaWorkload';

export interface NetworkBootType {
    bootType: string;
    duration: number;
}

/**
 * This function calculates network boot type based on two requests to analytics-ping
 * to eliminate browser clock from the calculation.
 */
export async function getNetworkBootTypeUsingModulePing(): Promise<NetworkBootType> {
    let moduleNWBootType = null;
    let durationDiffUsed = null;
    if (isPWA()) {
        return createNetworkBootTypeResult('Pwa');
    }

    // First find the workload.
    const workload = getOwaWorkload();
    let moduleParam = null;

    // Now find the module param to append based on workload
    switch (workload) {
        case OwaWorkload.Calendar:
            moduleParam = 'calendar';
            break;
        case OwaWorkload.Mail:
            moduleParam = 'mail';
            break;
        case OwaWorkload.People:
            moduleParam = 'people';
    }
    try {
        // Setup two network calls, one with module param and one with current timestamp.
        const fetchUrlForCurrentTimestamp = `${getPackageBaseUrl()}resources/analytics-ping.js?${Date.now()}`;
        const fetchUrlForModule = `${getPackageBaseUrl()}resources/${moduleParam}-analytics-ping.js`;

        const responses = await Promise.all([
            fetch(fetchUrlForCurrentTimestamp),
            fetch(fetchUrlForModule),
        ]);

        // If the module request has SW header
        if (responses[1].headers.get('x-sw-active-cache')) {
            return createNetworkBootTypeResult('SwCache');
        }

        // Now we need to parse the date info from the response headers and compare
        const responseDateStringForCurrentTimestamp = responses[0]?.headers?.get('Date');
        const responseDateStringForModule = responses[1]?.headers?.get('Date');

        // If date info is not present, highly unlikely but still checking for it
        if (responseDateStringForModule == null || responseDateStringForCurrentTimestamp == null) {
            return createNetworkBootTypeResult('Unknown');
        }
        const responseDateTimestampForModule = Date.parse(responseDateStringForModule);
        const responseDateTimestampForCurrentTimestamp = Date.parse(
            responseDateStringForCurrentTimestamp
        );
        if (
            isNaN(responseDateTimestampForModule) ||
            isNaN(responseDateTimestampForCurrentTimestamp)
        ) {
            return createNetworkBootTypeResult('Unknown');
        }

        durationDiffUsed =
            responseDateTimestampForModule - responseDateTimestampForCurrentTimestamp;

        // Now if date time stamp is older than the diff will generally be negative.
        // But again keeping a check of 1 second as observed that two requests going one after the another,
        // and the last request was 1 second behind in time!!
        moduleNWBootType = durationDiffUsed >= -1000 ? 'NoCache' : 'BrowserCache';
        return createNetworkBootTypeResult(moduleNWBootType, durationDiffUsed);
    } catch (e) {
        return createNetworkBootTypeResult('Unknown');
    }
}

function createNetworkBootTypeResult(bootType: string, duration?: number): NetworkBootType {
    return {
        bootType,
        duration,
    };
}
